import React from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import PageHeader from '../../components/PageHeader/PageHeader';

import { ReactComponent as HeaderIcon }  from '../../assets/site-assets/study/header-icon.svg';
import { ReactComponent as Highlights1 } from '../../assets/site-assets/study/highlights-1.svg';
import { ReactComponent as Highlights2 } from '../../assets/site-assets/study/highlights-2.svg';
import { ReactComponent as Highlights3 } from '../../assets/site-assets/study/highlights-3.svg';
import { ReactComponent as Highlights4 } from '../../assets/site-assets/study/highlights-4.svg';
import { ReactComponent as Highlights5 } from '../../assets/site-assets/study/highlights-5.svg';
import { ReactComponent as Highlights6 } from '../../assets/site-assets/study/highlights-6.svg';
import { ReactComponent as Telescope }   from '../../assets/site-assets/study/telescope.svg';

import BgImage  from '../../assets/site-assets/study/bg.webp';
import BookBg   from '../../assets/site-assets/study/book.svg';
import Uni1     from '../../assets/site-assets/study/Universities-1.webp';
import Uni2     from '../../assets/site-assets/study/Universities-2.webp';
import Uni3     from '../../assets/site-assets/study/Universities-3.webp';
import Uni4     from '../../assets/site-assets/study/Universities-4.webp';

import './StudyPage.scss';

const highlightIcons = [Highlights1, Highlights2, Highlights3, Highlights4, Highlights5, Highlights6];
const universities = [
  { logo: Uni1, href: 'https://www.alqasimia.ac.ae/ar/Pages/default.aspx' },
  { logo: Uni2, href: 'https://www.sma.ac.ae/ar' },
  { logo: Uni3, href: 'https://www.sharjah.ac.ae/ar' },
  { logo: Uni4, href: 'https://hct.ac.ae/ar/' },
];

const StudyPage = () => {
  const { t, i18n } = useTranslation();

  return (
    <div className="study-page">

      <PageHeader
        bgImage={BgImage}
        title={t('study.pageTitle')}
        subtitle={t('study.subtitle')}
      />

      {/* ── Leader + Stats ── */}
      <section className="study-intro">
        <HeaderIcon className="study-intro__sunflower" aria-hidden="true" />

        <div className="study-container">
          <div className="row g-5 align-items-start">

            <div className="col-12 col-lg-6">
              <span className="study-intro__hh">{t('study.intro.label')}</span>
              <h2 className="study-intro__name">
                {t('study.intro.nameLine1')}<br />{t('study.intro.nameLine2')}
              </h2>
              <p className="study-intro__role">
                {t('study.intro.rolePre')}{' '}
                <span>{t('study.intro.roleHighlight')}</span>
              </p>
              <p className="study-intro__body">{t('study.intro.body')}</p>
            </div>

            <div className="col-12 col-lg-6">
              <p className="study-intro__stats-label">
                {t('study.intro.statLabelLine1')}<br />{t('study.intro.statLabelLine2')}
              </p>
              <span className="study-intro__years">{t('study.intro.statDuration')}</span>
              <div className="study-intro__cards">
                <div className="study-intro__stat-card" style={{ backgroundImage: `url(${BookBg})` }}>
                  <span className="study-intro__stat-num">{t('study.intro.stat1Count')}</span>
                  <div className="study-intro__stat-sub-note-box">
                    <span className="study-intro__stat-sub">{t('study.intro.stat1Sub')}</span>
                    <span className="study-intro__stat-note">{t('study.intro.stat1Note')}</span>
                  </div>
                </div>
                <div className="study-intro__stat-card" style={{ backgroundImage: `url(${BookBg})` }}>
                  <span className="study-intro__stat-num">{t('study.intro.stat2Count')}</span>
                  <div className="study-intro__stat-sub-note-box">
                    <span className="study-intro__stat-sub">{t('study.intro.stat2Sub')}</span>
                    <span className="study-intro__stat-note">{t('study.intro.stat2Note')}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Various School Curricula ── */}
      <section className="study-curricula">
        <div className="study-container">
          <div className="study-curricula__inner">
            <h3 className="study-curricula__title">{t('study.curricula.title')}</h3>
            <p className="study-curricula__body">{t('study.curricula.body')}</p>
          </div>
        </div>
      </section>

      {/* ── Highlights ── */}
      <section className="study-highlights">
        <div className="study-container">
          <h2 className="study-highlights__title">
            {t('study.highlights.titlePre')}{' '}
            <span>{t('study.highlights.titleHighlight')}</span>{' '}
            {t('study.highlights.titlePost')}
          </h2>
          <div className="row g-4 g-lg-5 justify-content-center">
            {highlightIcons.map((Icon, i) => (
              <div key={i} className="col-6 col-sm-4 col-lg-4">
                <div className="study-highlights__item">
                  <div className="study-highlights__icon-wrap">
                    <Icon className="study-highlights__icon" />
                  </div>
                  <span className="study-highlights__label">{t(`study.highlights.items.${i}`)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── World-Class National Universities ── */}
      <section className="study-universities">
        <Telescope className="study-universities__telescope" aria-hidden="true" />
        <div className="study-container study-universities__content">
          <h2 className="study-universities__title">
            {t('study.universities.titlePre')}{' '}
            <span>{t('study.universities.titleHighlight')}</span>{' '}
            {t('study.universities.titlePost')}
          </h2>
          <p className="study-universities__body">{t('study.universities.body')}</p>

          <Swiper
            className="study-universities__swiper"
            modules={[Pagination]}
            spaceBetween={32}
            pagination={{ clickable: true }}
            breakpoints={{
              0:   { slidesPerView: 1 },
              576: { slidesPerView: 2 },
              992: { slidesPerView: 3 },
            }}
            key={i18n.language}
            dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
          >
            {universities.map((uni, i) => (
              <SwiperSlide key={i}>
                <a
                  href={uni.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="study-universities__logo-wrap"
                >
                  <img
                    src={uni.logo}
                    alt={t('study.universities.logoAlt', { number: i + 1 })}
                    className="study-universities__logo"
                  />
                </a>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

    </div>
  );
};

export default StudyPage;
